import * as React from 'react';
import Box from '@mui/material/Box';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Switch,
  Theme,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { ReactComponent as CreateIcon } from '../../../assets/icons/Create.svg';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { ReportingPeriod, ReportingPeriodGroup } from '@esg/esg-global-types';
import {
  checkReportingPeriodOverlap,
  createReportingPeriod,
  deleteReportingPeriod,
  getReportingPeriods,
  reporting_period_label,
  updateReportingPeriod
} from '../../../lib/metric_capture/reporting_period';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { ReactComponent as EditIcon } from '../../../assets/icons/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete.svg';
import DeleteConfirmModal from '../../shared/modal/DeleteConfirmModal';
import ConfigAddWidget, { CreateInput } from '../ConfigAddWidget';
import moment, { Moment } from 'moment';
import ConfigEditWidget, { EditInput } from '../ConfigEditWidget';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { DocumentReference } from 'firebase/firestore';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridValueFormatterParams
} from '@mui/x-data-grid';
import PrimaryButton from '../../shared/button/PrimaryButton';
import GhostButton from '../../shared/button/GhostButton';
import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';

const PanelReportingPeriods = () => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const [reportingPeriodRows, setReportingPeriodRows] = React.useState<Array<ReportingPeriod>>([]);
  const [displayWidgetPanelRight, setDisplayWidgetPanelRight] = React.useState(false);
  const [isCreateWidget, setIsCreateWidget] = React.useState(true);
  const [openDialogDelete, setOpenDialogDelete] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState<boolean>(false);
  const [actionRow, setActionRow] = React.useState<ReportingPeriod | null>(null);
  const [showLockConfirmModal, setShowLockConfirmModal] = React.useState(false);
  const [gridLoading, setGridLoading] = React.useState<boolean>(true);

  const theme: Theme = useTheme();
  const primary_blue: string = theme.palette.primary.main;

  // Handler Functions
  const handleCreateClick = (): void => {
    setActionRow(null);
    setDisplayWidgetPanelRight(true);
    setIsCreateWidget(true);
  };

  const handleEditClick = (reporting_period: ReportingPeriod): void => {
    setActionRow(reporting_period);
    setIsCreateWidget(false);
    setDisplayWidgetPanelRight(true);
  };

  const handleDeleteClick = (reporting_period: ReportingPeriod): void => {
    setActionRow(reporting_period);
    setOpenDialogDelete(true);
  };

  // Row functions
  const fetchRows = async () => {
    setGridLoading(true);
    try {
      if (group && company) {
        // Load rows from database.
        const reporting_periods: Array<ReportingPeriod> = await getReportingPeriods(
          group.id,
          company.id
        );
        // Load rows to memory.
        setReportingPeriodRows(reporting_periods);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelReportingPeriods failed on an unknown error while calling fetchRows.',
          {
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch reporting periods. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      return;
    } finally {
      setGridLoading(false);
    }
  };

  React.useEffect(() => {
    try {
      (async () => {
        setReportingPeriodRows([]);
        await fetchRows();
      })().catch((err: unknown) => {
        throw new Error(
          err instanceof Error
            ? err.message
            : 'Error: PanelReportingPeriods failed on an unknown error while calling fetchRows.'
        );
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelReportingPeriods failed on an unknown error while initialising.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  }, [group, company]);

  const columns: Array<GridColDef> = [
    {
      field: 'locked',
      headerName: 'Locked',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.locked ? 'Locked' : 'Lock'}>
            <Switch
              checked={params.row.locked ? true : false}
              onChange={() => {
                setActionRow(params.row);
                setShowLockConfirmModal(true);
              }}
            />
          </Tooltip>
        );
      }
    },
    {
      field: 'name',
      headerName: 'Name',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'start',
      headerName: 'Start',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      valueFormatter: (params: GridValueFormatterParams) => {
        return moment(params.value).format('DD MMM YYYY').toString();
      }
    },
    {
      field: 'end',
      headerName: 'End',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      valueFormatter: (params: GridValueFormatterParams) => {
        return moment(params.value).format('DD MMM YYYY').toString();
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            <GridActionsCellItem
              key={1}
              icon={
                <Tooltip title="Edit Reporting Period">
                  <EditIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Edit"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleEditClick(row)}
              disabled={row.locked}
            />
            <GridActionsCellItem
              key={2}
              icon={
                <Tooltip title="Delete Reporting Period">
                  <DeleteIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Delete"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleDeleteClick(row)}
            />
          </>
        ];
      }
    }
  ];

  const create_inputs: readonly CreateInput[] = [
    {
      id: 'name',
      type: 'text',
      label: 'Name',
      textValidation: (input_value) => typeof input_value === 'string' && input_value.length <= 14,
      defaultValue: moment().startOf('month').format('MMM YYYY')
    },
    {
      id: 'start_date',
      type: 'month_year',
      label: 'Month',
      defaultValue: moment().startOf('month'),
      onChange: (value, current_map) => {
        if (moment.isMoment(value)) {
          const formatted_name: string = value.format('MMM YYYY');
          if (current_map['name'] !== formatted_name) {
            return {
              ...current_map,
              name: formatted_name
            };
          }
        }
        return current_map;
      }
    }
  ];

  const edit_inputs: Array<EditInput> = [
    {
      id: 'name',
      type: 'text',
      label: 'Name',
      defaultValue: actionRow?.name,
      textValidation: (input_value) => typeof input_value === 'string' && input_value.length <= 14
    },
    {
      id: 'start',
      type: 'month_year',
      label: 'Month',
      defaultValue: moment(actionRow?.start).startOf('month')
    }
  ];

  const toggleLockRow = async () => {
    try {
      if (actionRow) {
        setActionLoading(true);
        await updateRow({ ...actionRow, locked: !actionRow.locked }, { ...actionRow });
      } else {
        throw new Error('Error updating Reporting Period, try again later');
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelReportingPeriods failed on an unknown error while calling toggleLockRow.',
          {
            actionRow: actionRow,
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to lock reporting period. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setShowLockConfirmModal(false);
      setActionLoading(false);
    }
  };

  const handleCloseLockConfirmModal = () => {
    setShowLockConfirmModal(false);
  };

  const handleClickBackDialogDelete = () => {
    setOpenDialogDelete(false);
  };

  const createRow = async (
    name: string,
    start_date: Moment,
    reporting_period_group: ReportingPeriodGroup | null
  ) => {
    try {
      const end_date: Moment = start_date.clone().endOf('month');

      if (group && company) {
        const overlapping_periods: Array<ReportingPeriod> = checkReportingPeriodOverlap(
          '1',
          start_date,
          end_date,
          reportingPeriodRows
        );
        if (overlapping_periods.length > 0) {
          setFeedbackData({
            message: `Cannot create Reporting Period overlapping: ${overlapping_periods.map((overlap) => overlap.name).join(', ')}`,
            state: true,
            type: 'error'
          });
          return;
        }
        const new_reporting_period: DocumentReference = await createReportingPeriod(
          group.id,
          company.id,
          {
            name: name,
            deleted: null,
            locked: false,
            start: start_date.toDate(),
            end: end_date.toDate(),
            reporting_period_group: reporting_period_group ?? undefined
          }
        ).catch((error) => {
          throw new Error(error);
        });
        if (new_reporting_period) {
          handleUpdateRows([
            {
              id: new_reporting_period.id,
              deleted: null,
              name: name,
              start: moment(start_date),
              end: moment(end_date),
              reporting_period_group: reporting_period_group ? reporting_period_group : undefined,
              locked: false
            },
            ...reportingPeriodRows
          ]);
        }
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelReportingPeriods failed on an unknown error while calling updateRow.',
          {
            group: group,
            company: company,
            name: name,
            start_date: start_date
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to create reporting period. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const updateRow = async (updated_row: ReportingPeriod, original_data: ReportingPeriod) => {
    try {
      const start_date: Moment = moment(updated_row.start);
      const end_date: Moment = start_date.clone().endOf('month');

      updated_row = {
        ...updated_row,
        start: start_date,
        end: end_date
      };

      if (group && company) {
        const overlapping_periods: Array<ReportingPeriod> = checkReportingPeriodOverlap(
          updated_row.id,
          updated_row.start,
          updated_row.end,
          reportingPeriodRows
        );
        if (overlapping_periods.length > 0) {
          setFeedbackData({
            message: `Cannot create Reporting Period overlapping: ${overlapping_periods.map((overlap) => overlap.name).join(', ')}`,
            state: true,
            type: 'error'
          });
          return;
        }
        await updateReportingPeriod(group.id, company.id, updated_row, original_data).then(() => {
          handleUpdateRows(
            reportingPeriodRows.map((row) => (updated_row.id === row.id ? { ...updated_row } : row))
          );
        });
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelReportingPeriods failed on an unknown error while calling updateRow.',
          {
            group: group,
            company: company,
            updated_row: updated_row,
            original_data: original_data
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to update reporting period. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const handleUpdateRows = (rows_data: Array<ReportingPeriod>) => {
    setReportingPeriodRows(rows_data);
  };

  const deleteRow = async (delete_reporting_period: ReportingPeriod) => {
    try {
      if (group && company && delete_reporting_period) {
        setActionLoading(true);
        await deleteReportingPeriod(
          group.id,
          company.id,
          delete_reporting_period.id,
          delete_reporting_period.name
        )
          .then(() => {
            handleUpdateRows(
              reportingPeriodRows.filter((reporting_period: ReportingPeriod) => {
                return reporting_period.id !== delete_reporting_period.id;
              })
            );
          })
          .catch((error) => {
            throw new Error(error);
          });
      } else {
        throw new Error('Error deleting reporting period, try again later');
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelReportingPeriods failed on an unknown error while calling deleteRow.',
          {
            group: group,
            company: company,
            'delete_reporting_period.id': delete_reporting_period.id,
            'delete_reporting_period.name': delete_reporting_period.name
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to remove reporting period. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setActionLoading(false);
      setOpenDialogDelete(false);
    }
  };

  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={openDialogDelete}
        handleCloseDeleteModal={handleClickBackDialogDelete}
        delete_label={actionRow && actionRow.name}
        handleDelete={actionRow && (() => deleteRow(actionRow))}
        allow_archive={false}
        allow_delete={true}
      />

      {/* Side Widget Panel */}
      <React.Fragment key={'right_add'}>
        <Drawer
          anchor={'right'}
          open={displayWidgetPanelRight}
          onClose={() => {
            setDisplayWidgetPanelRight(false);
          }}
          PaperProps={{ style: { width: '40%', padding: '1.5rem' } }}
        >
          {/* Create / Edit Widget */}
          {isCreateWidget ? (
            <ConfigAddWidget
              widget_label={reporting_period_label.one}
              icon={<CreateIcon height={40} fill={primary_blue} stroke={primary_blue} />}
              inputs={create_inputs}
              handleClose={() => setDisplayWidgetPanelRight(false)}
              createFunction={(
                name: string,
                start_date: Moment,
                reporting_period_group: ReportingPeriodGroup | null
              ) => createRow(name, start_date, reporting_period_group)}
            />
          ) : (
            <ConfigEditWidget
              widget_label={reporting_period_label.one}
              icon={<EditIcon width={40} fill={primary_blue} />}
              handleClose={() => setDisplayWidgetPanelRight(false)}
              edit_entity={actionRow}
              inputs={edit_inputs}
              handleEditInput={(input_id: string, value: unknown) =>
                actionRow && setActionRow({ ...actionRow, [input_id]: value })
              }
              confirmEditFunction={(
                updated_reporting_period: ReportingPeriod,
                original_data: ReportingPeriod
              ) => updateRow(updated_reporting_period, original_data)}
            />
          )}
        </Drawer>
      </React.Fragment>

      {/* Lock Confirmation Modal */}
      <Dialog
        open={showLockConfirmModal}
        onClose={handleCloseLockConfirmModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Box sx={{ p: 1.5 }}>
          <DialogTitle id="alert-dialog-title">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >
              <LockOpenIcon sx={{ marginRight: '1rem' }} fontSize="medium" />
              <Typography variant="h6" color="inherit" align="left">
                {actionRow?.locked ? 'Unlock' : 'Lock'} {actionRow?.name ? actionRow.name : 'Row'}?
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              You are about to {actionRow?.locked ? 'unlock' : 'lock'}{' '}
              {actionRow?.name ? actionRow.name : 'row'} {actionRow?.locked ? 'from' : 'for'} your
              reporting periods.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <GhostButton onClick={handleCloseLockConfirmModal} outlined dark>
              Back
            </GhostButton>
            <PrimaryButton
              loading={actionLoading}
              onClick={toggleLockRow}
              color="error"
              autoFocus
              palette_color="error"
            >
              Confirm
            </PrimaryButton>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Interactive Data Table */}
      <DataGrid
        autoHeight
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 }
          },
          sorting: {
            sortModel: [{ field: 'modified', sort: 'desc' }]
          }
        }}
        hideFooter={reportingPeriodRows.length > 1 ? false : true}
        columnHeaderHeight={reportingPeriodRows.length < 1 ? 0 : 56}
        pageSizeOptions={[10, 25, 50, 100]}
        loading={gridLoading}
        rows={reportingPeriodRows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{
          toolbar: () => (
            <DataGridToolbar
              entity_label={reporting_period_label}
              handleCreate={handleCreateClick}
            />
          ),
          noRowsOverlay: () => <DataGridNoRows missing_label={reporting_period_label} />,
          loadingOverlay: () => <DataGridLoading columns={columns.length} />
        }}
        slotProps={{ toolbar: { handleCreate: handleCreateClick } }}
        sx={{ '&, [class^=MuiDataGrid]': { border: 'none' } }}
      />
    </>
  );
};

export default PanelReportingPeriods;
